import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { LIMITES } from '../limites';
import { Zap, Heart, Shield, Star, Repeat, Target, BookOpen, Users, HelpCircle, CheckCircle, XCircle } from 'lucide-react';
import './NounsInvasion.css';

// --- Configuración ---
const INITIAL_LIVES = 5;
const CATEGORIES = [
    { key: 'todos', name: 'Todos', icon: '🌍', colors: 'from-indigo-500 to-purple-500' },
    { key: 'casa', name: 'Casa', icon: '🏡', colors: 'from-blue-500 to-cyan-400' },
    { key: 'escuela', name: 'Escuela', icon: '🏫', colors: 'from-orange-500 to-amber-400' },
    { key: 'animales', name: 'Animales', icon: '🐾', colors: 'from-lime-500 to-green-500' },
    { key: 'comida', name: 'Comida', icon: '🍕', colors: 'from-red-500 to-rose-500' },
];
const DEFAULT_EMOJI = '🤓';

function NounsInvasion() {
    // --- Estados del Juego ---
    const [gameState, setGameState] = useState('menu');
    const [difficulty, setDifficulty] = useState('medium');
    const [gameMode, setGameMode] = useState(''); // <-- CAMBIO: Inicia vacío
    const [theme, setTheme] = useState('todos');
    const [customTheme, setCustomTheme] = useState('');
    
    const [words, setWords] = useState([]);
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [userInput, setUserInput] = useState('');
    
    const [answerHistory, setAnswerHistory] = useState([]);
    const [showCorrectionCard, setShowCorrectionCard] = useState(false);
    const [animatedPoints, setAnimatedPoints] = useState(0);

    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(INITIAL_LIVES);
    const [streak, setStreak] = useState(0);
    const [highScore, setHighScore] = useState(() => parseInt(localStorage.getItem('nounsHighScore') || '0', 10));

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    
    const inputRef = useRef(null);

    const currentWord = useMemo(() => words[currentWordIndex], [words, currentWordIndex]);

    useEffect(() => {
        if (gameState === 'playing' && !showCorrectionCard) {
            inputRef.current?.focus();
        }
    }, [gameState, showCorrectionCard, currentWordIndex]);

    const startGame = async () => {
        setIsLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const selectedTheme = customTheme.trim() || theme;
        try {
            // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
            const response = await fetch('/api/minigames/get-nouns-invasion-words', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ 
                    theme: selectedTheme, 
                    difficulty: difficulty, 
                    seen_singulars: [] 
                }),
            });
            if (!response.ok) throw new Error('No se encontraron palabras para esta configuración.');
            const data = await response.json();
            setWords(data);
            setCurrentWordIndex(0);
            setScore(0);
            setLives(INITIAL_LIVES);
            setStreak(0);
            setUserInput('');
            setAnswerHistory([]);
            setShowCorrectionCard(false);
            setGameState('playing');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnswer = () => {
        if (!userInput.trim() || showCorrectionCard || animatedPoints > 0) return;

        const isPluralMode = gameMode === 'plural';
        const correctAnswer = isPluralMode ? currentWord.plural : currentWord.singular;
        const isCorrect = userInput.trim().toLowerCase() === correctAnswer.toLowerCase();
        
        setAnswerHistory(prev => [...prev, { ...currentWord, userAnswer: userInput, isCorrect }]);

        if (isCorrect) {
            const points = 10 + streak * 2;
            setScore(s => s + points);
            setStreak(s => s + 1);
            setAnimatedPoints(points);
            setTimeout(() => {
                moveToNextWord();
            }, 1000);
        } else {
            setLives(l => l - 1);
            setStreak(0);
            if (lives - 1 <= 0) {
                endGame();
            } else {
                setShowCorrectionCard(true);
            }
        }
    };

    const moveToNextWord = () => {
        setAnimatedPoints(0);
        if (currentWordIndex < words.length - 1) {
            setCurrentWordIndex(i => i + 1);
            setUserInput('');
            setShowCorrectionCard(false);
        } else {
            endGame();
        }
    };

    const endGame = () => {
        if (score > highScore) {
            setHighScore(score);
            localStorage.setItem('nounsHighScore', score.toString());
        }

        // Guardar XP en el backend
        const saveScore = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                
                const correctAnswersCount = answerHistory.filter(item => item.isCorrect).length;
                
                if (correctAnswersCount > 0) {
                    await fetch('/api/minigames/save-score', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            game_name: 'nouns_invasion',
                            score: score,
                            correct_answers: correctAnswersCount,
                            total_questions: words.length
                        })
                    });
                }
            } catch (error) {
                console.error("Error guardando XP:", error);
            }
        };
        saveScore();

        setGameState('gameOver');
    };
    
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (showCorrectionCard) {
                moveToNextWord();
            } else {
                handleAnswer();
            }
        }
    };

    const resetGame = () => {
        setGameState('menu');
        setError('');
        setCustomTheme('');
        setGameMode(''); // Resetea el modo de juego
    };

    const handleThemeClick = (key) => {
        setTheme(key);
        setCustomTheme('');
    };

    const handleCustomThemeChange = (e) => {
        setCustomTheme(e.target.value);
        setTheme('');
    };

    // --- Renderizado de Componentes ---

    const renderMenu = () => (
        <div className="nouns-container">
            <div className="nouns-inner-container">
                <div className="nouns-menu">
                    <div className="nouns-header"><Shield size={40} /><h1>Nouns Invasion</h1><p>Aprende sustantivos y sus plurales para defenderte.</p></div>
                    <div className="nouns-dashboard">
                        <div><Star size={16} /><span>Points</span><strong>{highScore}</strong></div>
                        <div><Zap size={16} /><span>Racha</span><strong>0</strong></div>
                        <div><Heart size={16} /><span>Vidas</span><strong>{INITIAL_LIVES}</strong></div>
                    </div>
                    <div className="ni-config-section">
                        <h3>1. Elige la Dificultad</h3>
                        <div className="ni-difficulty-options">
                            <button onClick={() => setDifficulty('easy')} className={`easy ${difficulty === 'easy' ? 'active' : ''}`}>Easy</button>
                            <button onClick={() => setDifficulty('medium')} className={`medium ${difficulty === 'medium' ? 'active' : ''}`}>Medium</button>
                            <button onClick={() => setDifficulty('hard')} className={`hard ${difficulty === 'hard' ? 'active' : ''}`}>Hard</button>
                        </div>
                    </div>
                    <div className="ni-config-section">
                        <h3>2. Elige un Tema</h3>
                        <div className="ni-theme-grid">
                            {CATEGORIES.map(cat => (
                                <button key={cat.key} onClick={() => handleThemeClick(cat.key)} className={`ni-theme-btn ${theme === cat.key ? 'active' : ''}`}>
                                    <span className="theme-icon">{cat.icon}</span> <span className="theme-name">{cat.name}</span>
                                </button>
                            ))}
                        </div>
                        <input type="text" placeholder="O escribe un tema (ej: oficina, cocina...)" value={customTheme} onChange={handleCustomThemeChange} className="ni-theme-input" />
                            maxLength={LIMITES.contexto}
                    </div>
                    <div className="ni-config-section">
                        <h3>3. Elige el Modo de Juego</h3>
                        <div className="ni-config-options">
                            <button onClick={() => setGameMode('singular')} className={gameMode === 'singular' ? 'active' : ''}><BookOpen size={16}/> Nouns</button>
                            <button onClick={() => setGameMode('plural')} className={gameMode === 'plural' ? 'active' : ''}><Users size={16}/> Plural Nouns</button>
                        </div>
                    </div>
                    {error && <p className="error-message">{error}</p>}
                    <button className="nouns-play-btn" onClick={startGame} disabled={isLoading || !gameMode}>
                        {isLoading ? 'Cargando...' : '¡Comenzar Invasión!'}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderCorrectionCard = () => (
        <div className="ni-feedback-overlay">
            <div className="ni-feedback-card incorrect">
                <XCircle size={48} />
                <h2>¡Respuesta Incorrecta!</h2>
                <p>La respuesta correcta es:</p>
                <strong>{gameMode === 'plural' ? currentWord.plural : currentWord.singular}</strong>
                <button className="nouns-play-btn" onClick={moveToNextWord}>Continuar</button>
            </div>
        </div>
    );

    const renderGameOver = () => (
        <div className="ni-game-over-screen">
            <h2>¡Invasión Terminada!</h2>
            <p>Tu puntaje final fue:</p>
            <strong className="ni-final-score">{score}</strong>
            <div className="ni-results-list">
                <h3>Resumen de la Ronda</h3>
                {answerHistory.map((item, index) => (
                    <div key={index} className={`ni-result-item ${item.isCorrect ? 'correct' : 'incorrect'}`}>
                        <div className="result-item-word">
                            {item.isCorrect ? <CheckCircle size={20} /> : <XCircle size={20} />}
                            <span>{item.singular}</span>
                        </div>
                        <div className="result-item-answer">
                            {item.isCorrect 
                                ? <span className="correct-text">Well done!</span>
                                : <span>Tu respuesta: "{item.userAnswer}". Correcta: "{gameMode === 'plural' ? item.plural : item.singular}"</span>
                            }
                        </div>
                    </div>
                ))}
            </div>
            <button className="nouns-play-btn" onClick={resetGame}><Repeat size={16}/> Jugar de Nuevo</button>
        </div>
    );

    if (gameState === 'playing' || gameState === 'gameOver') {
        const promptText = gameMode === 'plural' 
            ? `Escribe el plural de "${currentWord?.singular}"` 
            : `¿Cómo se dice "${currentWord?.spanish}" en inglés?`;

        return (
            <div className="nouns-container">
                 <div className="nouns-inner-container playing">
                    {showCorrectionCard && renderCorrectionCard()}
                    <div className="ni-game-hud">
                        <div className="ni-hud-item score">
                            <Star size={16}/> {score}
                            {animatedPoints > 0 && <span className="points-animation">+{animatedPoints}</span>}
                        </div>
                        <div className="ni-hud-item lives">
                            {Array.from({ length: INITIAL_LIVES }).map((_, i) => (
                                <Heart key={i} size={20} className={i < lives ? 'filled' : 'empty'}/>
                            ))}
                        </div>
                        <div className="ni-hud-item streak"><Zap size={16}/> x{streak}</div>
                    </div>
                    <div className="ni-game-area">
                        {gameState === 'gameOver' ? renderGameOver() : (
                            <>
                                <div className="ni-word-card">
                                    <span className="word-emoji">{currentWord?.emoji || DEFAULT_EMOJI}</span>
                                    <p className="word-prompt">{promptText}</p>
                                    {gameMode === 'plural' && <p className="word-hint">(que en español es "{currentWord?.spanish}")</p>}
                                </div>
                                <div className="ni-answer-area">
                                    <input
                                        maxLength={LIMITES.respuestaMinijuego}
                                        ref={inputRef}
                                        type="text"
                                        className="ni-answer-input"
                                        value={userInput}
                                        onChange={e => setUserInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Escribe tu respuesta..."
                                        disabled={showCorrectionCard || animatedPoints > 0}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                 </div>
            </div>
        );
    }

    return renderMenu();
}

export default NounsInvasion;